/**
 * NotesSection v5.0 - localStorage draft + backend flag for supervisability
 * localStorage = source of truth for draft text (synchronous, no closure issues)
 * Backend = only stores a flag (for yellow rows + counter + supervisor visibility)
 * Key format: crm_draft_{agentPhone}_{contactPhone}
 */

import { useState, useEffect, useRef } from 'react'
import { notesAPI, authAPI, draftsAPI } from '../../services/api'
import DayPickerModal from '../shared/DayPickerModal'

// Chile timezone date helper
function getChileDate() {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Santiago', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()).split('-')
  return { year: +parts[0], month: +parts[1], day: +parts[2] }
}

function getChileTime() {
  const now = new Date()
  const hh = new Intl.DateTimeFormat('en', { timeZone: 'America/Santiago', hour: '2-digit', hour12: false }).format(now)
  const mm = new Intl.DateTimeFormat('en', { timeZone: 'America/Santiago', minute: '2-digit' }).format(now)
  return hh.padStart(2, '0') + ':' + mm.padStart(2, '0')
}

function chileToday() {
  const { year, month, day } = getChileDate()
  return year + '-' + String(month).padStart(2, '0') + '-' + String(day).padStart(2, '0')
}

function chileShortcut(days) {
  const { year, month, day } = getChileDate()
  const d = new Date(year, month - 1, day)
  d.setDate(d.getDate() + days)
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

function chileShortcutMonth(months) {
  const { year, month, day } = getChileDate()
  const d = new Date(year, month - 1, day)
  d.setMonth(d.getMonth() + months)
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

function formatDateShort(isoDate) {
  if (!isoDate) return ''
  const [y, m, d] = isoDate.split('-')
  return d + '/' + m
}

// Parsea la ultima linea "AS, DD/MM [HH:MM]" de un texto (espejo de utils/asNoteParser.js)
// y devuelve 'YYYY-MM-DD' + 'HH:MM' + source, para repoblar el estado de agendamiento al
// restaurar un borrador. Resuelve el anio contra la fecha Chile actual.
function parseAsLine(text) {
  if (!text || typeof text !== 'string') return { date: null, time: null, source: null }
  const regex = /\bAS[,\s]+(\d{1,2})\/(\d{1,2})(?:\s+(\d{1,2}):(\d{2}))?\b/g
  let last = null, m
  while ((m = regex.exec(text)) !== null) last = m
  if (!last) return { date: null, time: null, source: null }
  const dia = parseInt(last[1], 10), mes = parseInt(last[2], 10)
  if (dia < 1 || dia > 31 || mes < 1 || mes > 12) return { date: null, time: null, source: null }
  const { year: yNow, month: mNow } = getChileDate()
  let year = yNow
  if (mes < mNow && (mNow - mes) > 6) year = yNow + 1
  const date = String(year) + '-' + String(mes).padStart(2, '0') + '-' + String(dia).padStart(2, '0')
  let time = null
  if (last[3] != null && last[4] != null) {
    const hh = parseInt(last[3], 10), mn = parseInt(last[4], 10)
    if (hh >= 0 && hh <= 23 && mn >= 0 && mn <= 59) time = String(hh).padStart(2, '0') + ':' + String(mn).padStart(2, '0')
  }
  const upper = text.toUpperCase()
  let source = null
  if (upper.includes('NO CONTESTA')) source = 'no_contesta'
  else if (upper.includes('WSP') || upper.includes('WHATSAPP')) source = 'si_whatsapp'
  else if (upper.includes('SI CONTESTA')) source = 'si_contesta'
  else if (upper.includes('AGENDADO')) source = 'agendar'
  return { date, time, source }
}

export default function NotesSection({ phone, notes = [], onNoteAdded, onProspectUpdate, onDraftChanged, initialMessage, productInterest, noContestaDay, nextFollowupDate, onIcPendingEnqueue, contactName, currentIcon }) {
  const [mode, setMode] = useState('idle') // 'idle' | 'new' | 'edit'
  const [newNoteText, setNewNoteText] = useState('')
  const [editingNoteId, setEditingNoteId] = useState(null)
  const [editText, setEditText] = useState('')
  const [saving, setSaving] = useState(false)
  const [slowLevel, setSlowLevel] = useState(0) // 0=normal, 1=>3s lento, 2=>10s muy lento
  const [asInserted, setAsInserted] = useState(false) // AS was inserted
  const [showAsDatePicker, setShowAsDatePicker] = useState(false)
  const [asDate, setAsDate] = useState('')
  const [asTime, setAsTime] = useState('')
  const sabanaRef = useRef(null)
  const textareaRef = useRef(null)
  const [asModalSource, setAsModalSource] = useState(null) // 'agendar' | 'si_contesta' | 'no_contesta'
  const [pendingNoContestaDay, setPendingNoContestaDay] = useState(null)
  const [pendingButtonAction, setPendingButtonAction] = useState(null) // 'si_contesta'|'no_contesta'|'agendar' for stats
  const [pendingQuickPhrase, setPendingQuickPhrase] = useState([]) // array of active quick phrases
  const [asEditFollowup, setAsEditFollowup] = useState(null) // next_followup_date confirmado via modal AS en ESTA edicion (null = no confirmado). Guardamos el VALOR exacto, no un flag: re-abrir el modal muta asDate/asTime aunque se cancele, y reconstruir de ellos propagaria una fecha no confirmada.

  // --- Draft helpers (localStorage) ---
  const getDraftKey = () => {
    try {
      const user = authAPI.getCurrentUser()
      const agentPhone = user?.phone || 'unknown'
      return 'crm_draft_' + agentPhone + '_' + phone
    } catch { return 'crm_draft_unknown_' + phone }
  }

  const getAgentName = () => {
    try {
      const user = authAPI.getCurrentUser()
      return user?.name || 'Agente'
    } catch { return 'Agente' }
  }

  // Format a note for display
  const formatNoteText = (note) => {
    const d = new Date(note.creada_en)
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const hh = String(d.getHours()).padStart(2, '0')
    const min = String(d.getMinutes()).padStart(2, '0')
    let text = dd + '/' + mm + ' - ' + (note.autor || 'Agente') + ' ' + hh + ':' + min + '\n' + note.nota
    if (note.accion_siguiente) {
      text += '\nAS, ' + note.accion_siguiente
      if (note.accion_siguiente_fecha) {
        const fd = note.accion_siguiente_fecha.split('T')[0]
        const [y, m, da] = fd.split('-')
        text += ' ' + da + '/' + m
      }
    }
    return text
  }

  const sortedNotes = [...notes].filter(n => n.autor !== 'Supervisor IA').reverse()

  // Scroll to bottom in idle mode (latest note visible)
  useEffect(() => {
    if (sabanaRef.current && mode === 'idle') {
      sabanaRef.current.scrollTop = sabanaRef.current.scrollHeight
    }
  }, [notes.length, mode])

  // Scroll to textarea when entering new mode
  useEffect(() => {
    if (mode === 'new') {
      // Use longer delay to ensure notes have rendered in sabana
      const timer = setTimeout(() => {
        // First scroll sabana to bottom to show latest notes
        if (sabanaRef.current) {
          sabanaRef.current.scrollTop = sabanaRef.current.scrollHeight
        }
        // Then scroll textarea into view
        if (textareaRef.current) {
          textareaRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          textareaRef.current.focus()
        }
      }, 400)
      return () => clearTimeout(timer)
    }
  }, [mode])

  // --- DRAFT: On MOUNT, restore from localStorage ---
  useEffect(() => {
    const key = getDraftKey()
    const savedDraft = localStorage.getItem(key)
    if (savedDraft) {
      setNewNoteText(savedDraft)
      setMode('new') // mode change effect will handle scroll
      // Si el draft ya tiene una linea AS, marcar asInserted para que Guardar
      // se habilite sin que el agente tenga que volver a clickear Si Contesta/etc.
      // Pattern: linea que empieza con "AS, " (precedida de inicio o newline).
      if (/(^|\n)AS,\s/.test(savedDraft)) {
        setAsInserted(true)
        // Bug fix (2026-06-04): el borrador persiste solo el TEXTO; sin esto, al restaurar
        // un draft con linea AS el estado asDate/asTime/pendingButtonAction quedaba vacio y
        // Guardar NO actualizaba el venc (next_followup_date). Re-derivamos del texto.
        const parsedAs = parseAsLine(savedDraft)
        if (parsedAs.date) {
          setAsDate(parsedAs.date)
          if (parsedAs.time) setAsTime(parsedAs.time)
          if (parsedAs.source) setPendingButtonAction(parsedAs.source)
        }
      }
      // Restore quick phrase selection if saved
      const savedQP = localStorage.getItem(key + '_qp')
      if (savedQP) { try { const arr = JSON.parse(savedQP); setPendingQuickPhrase(Array.isArray(arr) ? arr : [savedQP]) } catch(e) { setPendingQuickPhrase([savedQP]) } }
      // Restore Dia N (no_contesta_dia) si se guardo (No/Si Contesta). No es derivable del texto AS.
      const savedNcd = localStorage.getItem(key + '_ncd')
      if (savedNcd) { const n = parseInt(savedNcd, 10); if (!isNaN(n)) setPendingNoContestaDay(n) }
    }
  }, []) // Only on mount (key-based remount handles contact changes)

  // --- DRAFT: On text change, save to localStorage immediately ---
  useEffect(() => {
    const key = getDraftKey()
    if (mode === 'new' && newNoteText.trim()) {
      localStorage.setItem(key, newNoteText)
      // Persist quick phrase selection alongside draft
      if (pendingQuickPhrase.length) localStorage.setItem(key + '_qp', JSON.stringify(pendingQuickPhrase))
      else localStorage.removeItem(key + '_qp')
      // Persist Dia N (no_contesta_dia) alongside draft. != null (no truthy):
      // el Día 0 (reset por Si Contesta/Si WSP) debe persistir en el borrador para
      // que al restaurar+guardar el reset se mande igual. El restore (savedNcd "0") ya lo maneja.
      if (pendingNoContestaDay != null) localStorage.setItem(key + '_ncd', String(pendingNoContestaDay))
      else localStorage.removeItem(key + '_ncd')
    }
  }, [newNoteText, mode, pendingQuickPhrase, pendingNoContestaDay])

  // --- DRAFT: On UNMOUNT, set backend flag if draft exists ---
  useEffect(() => {
    return () => {
      const key = getDraftKey()
      const draft = localStorage.getItem(key)
      if (draft && draft.trim()) {
        // Fire-and-forget: set backend flag for yellow row / counter
        draftsAPI.saveDraft(phone, 'pending').catch(() => {})
        if (onDraftChanged) setTimeout(() => onDraftChanged(), 100)
      }
    }
  }, [phone]) // phone in deps so cleanup captures the correct phone value

  // --- DRAFT: beforeunload - set backend flag on page close ---
  useEffect(() => {
    const handler = () => {
      const key = getDraftKey()
      const draft = localStorage.getItem(key)
      if (draft && draft.trim()) {
        // [F2] Sesion por cookie httpOnly (viaja sola, incluso con keepalive)
        fetch('/api/crm-web/notes/draft', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ contacto_phone: phone, draft_text: 'pending' }),
          keepalive: true
        })
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [phone])

  // New note
  const handleNewNote = () => {
    const { day, month } = getChileDate()
    const time = getChileTime()
    const agent = getAgentName()
    const header = String(day).padStart(2, '0') + '/' + String(month).padStart(2, '0') + ' - ' + agent + ' ' + time
    setNewNoteText(header + '\n')
    setMode('new')
    setAsInserted(false)
    setPendingQuickPhrase([])
    setEditingNoteId(null)
    setEditText('')
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length)
      }
    }, 100)
  }

  // Edit mode
  const handleEditMode = () => {
    setMode('edit')
    setAsInserted(true) // editing existing notes doesn't require new AS
    setNewNoteText('')
    // Higiene: entrar a editar parte SIN agendamiento pendiente (asDate/asTime podian quedar
    // stale de un flujo previo; el boton Agendar pre-rellena asDate antes de abrir el modal).
    setAsEditFollowup(null)
    setAsDate('')
    setAsTime('')
    setPendingNoContestaDay(null)
    setPendingButtonAction(null)
  }

  const handleNoteClick = (note) => {
    if (mode !== 'edit') return
    if (editingNoteId === note.id) return
    setEditingNoteId(note.id)
    // Cambiar de nota descarta cualquier AS confirmado para la nota anterior.
    setAsEditFollowup(null)
    setAsDate('')
    setAsTime('')
    setPendingNoContestaDay(null)
    setPendingButtonAction(null)
    let text = note.nota || ''
    if (note.accion_siguiente) {
      text += '\nAS, ' + note.accion_siguiente
    }
    setEditText(text)
    setTimeout(() => textareaRef.current?.focus(), 50)
  }

  // Agendar button -> open date picker
  const handleAgendarClick = () => {
    setAsDate(chileShortcut(1))
    setAsTime('')
    setAsModalSource('agendar')
    setShowAsDatePicker(true)
  }

  // Si Contesta -> open date picker
  // NOTA: DayPickerModal resetea selectedNoContestaDay a '' al abrir.
  // El auto-select Día 0 cuando noContestaDay>0 queda diferido a T5 (requiere prop initialDay en DayPickerModal).
  const handleSiContestaClick = () => {
    setAsDate(chileShortcut(1))
    setAsTime('')
    setAsModalSource('si_contesta')
    setShowAsDatePicker(true)
  }

  // No Contesta -> open date picker with day selector
  const handleNoContestaClick = () => {
    setAsDate(chileShortcut(1))
    setAsTime('')
    setAsModalSource('no_contesta')
    setShowAsDatePicker(true)
  }

  // Si WSP (contestó por WhatsApp / conversación activa) -> mismo flujo que Si Contesta
  const handleSiWspClick = () => {
    setAsDate(chileShortcut(1))
    setAsTime('')
    setAsModalSource('si_whatsapp')
    setShowAsDatePicker(true)
  }

  // Confirm AS date selection — recibe parámetros desde DayPickerModal.onConfirm(day, date, time)
  const handleASConfirmCore = (day, dateArg, timeArg) => {
    if (!dateArg) return
    // Validate HH:MM format (ignore partial input)
    const effectiveTime = /^\d{2}:\d{2}$/.test(timeArg || '') ? timeArg : ''
    if (effectiveTime) setAsTime(effectiveTime)
    setAsDate(dateArg)
    const fmtShort = formatDateShort(dateArg) + (effectiveTime ? ' ' + effectiveTime : '')
    const suffix = asModalSource === 'agendar' ? 'Agendado'
                 : asModalSource === 'si_contesta' ? 'Si Contesta'
                 : asModalSource === 'si_whatsapp' ? 'Si WSP'
                 : (day ? 'No Contesta (DIA ' + day + ')' : 'No Contesta')
    // Trailing space para que el agente pueda continuar escribiendo sin presionar barra
    const asLine = suffix + '\nAS, ' + fmtShort + ' '
    setPendingButtonAction(asModalSource) // remember which button was pressed for stats

    if (mode === 'new') {
      setNewNoteText(prev => {
        const trimmed = prev.trimEnd()
        return trimmed + '\n' + asLine
      })
    } else if (mode === 'edit' && editingNoteId) {
      setEditText(prev => {
        const trimmed = prev.trimEnd()
        return trimmed + '\n' + asLine
      })
      // Captura el next_followup_date EXACTO confirmado ahora (valor, no flag): inmune a que
      // re-abrir el modal Agendar mute asDate/asTime. Un re-confirm lo sobre-escribe; cancelar no.
      setAsEditFollowup(dateArg + (effectiveTime ? 'T' + effectiveTime + ':00' : ''))
    }

    if ((asModalSource === 'no_contesta' || asModalSource === 'si_contesta' || asModalSource === 'si_whatsapp') && day != null) {
      setPendingNoContestaDay(day)
    }

    setAsInserted(true)
    setShowAsDatePicker(false)
    setAsModalSource(null)
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length)
      }
    }, 50)
  }

  // Slow-save indicator: escalate label/color when save takes > 3s / 10s
  useEffect(() => {
    if (!saving) {
      setSlowLevel(0)
      return
    }
    const t1 = setTimeout(() => setSlowLevel(1), 3000)
    const t2 = setTimeout(() => setSlowLevel(2), 10000)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [saving])

  // Save
  const handleSave = async () => {
    if (mode === 'new') {
      const lines = newNoteText.split('\n')
      const content = lines.slice(1).join('\n').trim()
      if (!content) return
      setSaving(true)
      try {
        // Send AS date to auto-update next_followup_date
        const rawSaveTime = asTime || ''
        const effectiveSaveTime = /^\d{2}:\d{2}$/.test(rawSaveTime) ? rawSaveTime : ''
        const applyActions = asDate ? [{ type: 'followup_date', value: asDate + (effectiveSaveTime ? 'T' + effectiveSaveTime + ':00' : ''), display: 'AS fecha' }] : []
        if (pendingNoContestaDay != null) {
          // != null (no falsy): Si Contesta/Si WSP confirman Día 0 → 0 es falsy en JS y
          // antes se descartaba, dejando el DÍA N viejo sin resetear. Ahora persiste el 0.
          applyActions.push({ type: 'no_contesta_dia', value: pendingNoContestaDay, display: 'Dia ' + pendingNoContestaDay })
        }
        if (pendingButtonAction) {
          applyActions.push({ type: 'button_click', value: pendingButtonAction, display: 'Click ' + pendingButtonAction })
        }
        if (pendingQuickPhrase.length) {
          pendingQuickPhrase.forEach(p => applyActions.push({ type: 'button_click', value: p, display: 'Quick ' + p }))
        }
        const noteRes1 = await notesAPI.addNote(phone, content, applyActions, null, { markIcPending: true })
        if (onIcPendingEnqueue && noteRes1?.note?.id) {
          onIcPendingEnqueue(noteRes1.note.id, { phone, contact_name: contactName || phone, current_icon: currentIcon || null })
        }
        // Notificar al padre (TodoListView via ProspectPanel.handleNotesUpdate) los
        // cambios aplicados para que el optimistic update se vea en la grilla
        // inmediatamente. Bug C (2026-05-15): antes solo pasabamos nextFollowupDate;
        // no_contesta_dia se quedaba sin reflejar hasta el silentRefresh.
        if (onProspectUpdate) {
          const extras = {}
          if (asDate) {
            extras.nextFollowupDate = asDate + (effectiveSaveTime ? 'T' + effectiveSaveTime + ':00' : '')
          }
          if (pendingNoContestaDay != null) {
            extras.no_contesta_dia = Number(pendingNoContestaDay)
          }
          if (Object.keys(extras).length > 0) onProspectUpdate(extras)
        }
        setMode('idle')
        setNewNoteText('')
        setAsInserted(false)
        setAsDate('')
        setAsTime('')
        setPendingNoContestaDay(null)
        setPendingButtonAction(null)
        setPendingQuickPhrase([])
        // Clear localStorage draft + backend flag
        const key = getDraftKey()
        localStorage.removeItem(key)
        localStorage.removeItem(key + '_qp')
        localStorage.removeItem(key + '_ncd')
        draftsAPI.deleteDraft(phone).catch(() => {}).finally(() => {
          if (onDraftChanged) onDraftChanged()
        })
        if (onNoteAdded) onNoteAdded()
      } catch (err) {
        console.error('Error saving note:', err)
      } finally {
        setSaving(false)
      }
    } else if (mode === 'edit' && editingNoteId) {
      const trimmed = editText.trim()
      if (!trimmed) return
      setSaving(true)
      try {
        // Propagar el agendamiento SOLO si el modal AS se confirmo en esta edicion, usando el
        // VALOR capturado (asEditFollowup), no asDate/asTime. El texto "AS, dd/mm" NO es senal
        // (toda nota vieja lo contiene) y asDate/asTime tampoco (el boton Agendar pre-rellena
        // asDate y re-abrir/cancelar el modal los corrompe). asEditFollowup solo cambia al CONFIRMAR.
        const applyActions = []
        let followupValue = null
        if (asEditFollowup) {
          followupValue = asEditFollowup
          applyActions.push({ type: 'followup_date', value: followupValue, display: 'AS fecha (edicion)' })
          if (pendingNoContestaDay != null) {
            applyActions.push({ type: 'no_contesta_dia', value: pendingNoContestaDay, display: 'Dia ' + pendingNoContestaDay })
          }
        }
        await notesAPI.updateNote(phone, editingNoteId, trimmed, applyActions)
        // Optimistic update -> la grilla refleja VENC/HORA al instante (el sintoma reportado).
        // Mismo shape que la rama 'new': nextFollowupDate -> handleProspectUpdate lo mapea a
        // next_followup_date y lo trata como accion de agente (frozen/contadores).
        if (onProspectUpdate && followupValue) {
          const extras = { nextFollowupDate: followupValue }
          if (pendingNoContestaDay != null) extras.no_contesta_dia = Number(pendingNoContestaDay)
          onProspectUpdate(extras)
        }
        setMode('idle')
        setEditingNoteId(null)
        setEditText('')
        setAsInserted(false)
        setAsEditFollowup(null)
        setAsDate('')
        setAsTime('')
        setPendingNoContestaDay(null)
        setPendingButtonAction(null)
        if (onNoteAdded) onNoteAdded()
      } catch (err) {
        console.error('Error updating note:', err)
      } finally {
        setSaving(false)
      }
    }
  }

  // "Hoy" button: save note keeping current next_followup_date, append AS line at end
  const handleHoyClick = async () => {
    if (!nextFollowupDate || saving || mode !== 'new') return

    // Skip line 0 (auto-generated header from handleNewNote) — el render lo agrega solo
    const lines = newNoteText.split('\n')
    const bodyText = lines.slice(1).join('\n').trim()

    // Build AS line por string-parse (sin TZ conversion).
    // El backend serializa `timestamp without time zone` con sufijo Z (engañoso) — el valor
    // crudo del split después de "T" YA representa la hora Chile que el agente eligió.
    // Por eso NO usamos new Date() ni Intl: causarían un offset incorrecto de UTC↔Chile.
    let asLine = 'AS, '
    const datePart = String(nextFollowupDate).split('T')[0]
    const timePart = String(nextFollowupDate).split('T')[1] || ''
    const [, m, da] = datePart.split('-')
    asLine += `${da}/${m}`
    if (timePart) {
      const [hh, mn] = timePart.split(':')
      if (hh && mn && (hh !== '00' || mn !== '00')) asLine += ` ${hh}:${mn}`
    }

    const hasAS = bodyText.split('\n').some(l => l.trim().startsWith('AS,'))
    // Empty body permitido: guardamos solo la AS line (marker "agente revisó hoy")
    const finalBody = bodyText
      ? (hasAS ? bodyText : bodyText + '\n' + asLine)
      : asLine

    setSaving(true)
    try {
      const applyActions = []
      if (pendingQuickPhrase.length) {
        pendingQuickPhrase.forEach(p => applyActions.push({ type: 'button_click', value: p, display: 'Quick ' + p }))
      }
      applyActions.push({ type: 'button_click', value: 'hoy', display: 'Click hoy' })
      // No followup_date action: keeps current next_followup_date intact
      const noteRes2 = await notesAPI.addNote(phone, finalBody, applyActions, null, { markIcPending: true })
      if (onIcPendingEnqueue && noteRes2?.note?.id) {
        onIcPendingEnqueue(noteRes2.note.id, { phone, contact_name: contactName || phone, current_icon: currentIcon || null })
      }
      setMode('idle')
      setNewNoteText('')
      setAsInserted(false)
      setAsDate('')
      setAsTime('')
      setPendingNoContestaDay(null)
      setPendingButtonAction(null)
      setPendingQuickPhrase([])
      const key = getDraftKey()
      localStorage.removeItem(key)
      localStorage.removeItem(key + '_qp')
      localStorage.removeItem(key + '_ncd')
      draftsAPI.deleteDraft(phone).catch(() => {}).finally(() => {
        if (onDraftChanged) onDraftChanged()
      })
      if (onNoteAdded) onNoteAdded()
    } catch (err) {
      console.error('Error saving Hoy note:', err)
    } finally {
      setSaving(false)
    }
  }

  const canHoy = () => {
    // Habilitado si hay fecha de vencimiento + modo new + no guardando.
    // Contenido vacío es OK: se guardará una nota mínima con AS line (marker "agente revisó hoy").
    return !saving && mode === 'new' && !!nextFollowupDate
  }

  const handleCancel = () => {
    // Clear localStorage draft + backend flag on cancel
    const key = getDraftKey()
    localStorage.removeItem(key)
    localStorage.removeItem(key + '_qp')
    localStorage.removeItem(key + '_ncd')
    draftsAPI.deleteDraft(phone).catch(() => {}).finally(() => {
      if (onDraftChanged) onDraftChanged()
    })
    setMode('idle')
    setNewNoteText('')
    setEditingNoteId(null)
    setEditText('')
    setAsInserted(false)
    setShowAsDatePicker(false)
    setAsModalSource(null)
    setAsEditFollowup(null)
    setAsDate('')
    setAsTime('')
    setPendingNoContestaDay(null)
    setPendingButtonAction(null)
    setPendingQuickPhrase([])
  }

  // Quick phrase toggle: insert/remove phrases (multiple allowed)
  const QUICK_PHRASE_MAP = { me_llamo: 'ME LLAM\u00D3', le_llame: 'LE LLAM\u00C9', me_escribio: 'ME ESCRIBI\u00D3', le_escribi: 'LE ESCRIB\u00CD', envie_cotizacion: 'ENVI\u00C9 COTIZACI\u00D3N', pidio_cotizacion: 'PIDI\u00D3 COTIZACI\u00D3N' }
  const insertQuickPhrase = (phraseKey, phraseText) => {
    const setter = mode === 'new' ? setNewNoteText : (mode === 'edit' && editingNoteId) ? setEditText : null
    if (!setter) return

    // Toggle off: same button clicked again -> remove text + deselect
    if (pendingQuickPhrase.includes(phraseKey)) {
      setter(prev => {
        const withNewline = '\n' + phraseText
        if (prev.includes(withNewline)) return prev.replace(withNewline, '')
        return prev.replace(phraseText, '')
      })
      setPendingQuickPhrase(prev => prev.filter(k => k !== phraseKey))
      setTimeout(() => { const ta = textareaRef.current; if (ta) { ta.focus(); ta.setSelectionRange(ta.value.length, ta.value.length) } }, 50)
      return
    }

    // Insert new phrase on new line (multiple allowed)
    setter(prev => {
      const trimmed = prev.trimEnd()
      return trimmed + '\n' + phraseText + ' '
    })
    setPendingQuickPhrase(prev => [...prev, phraseKey])
    setTimeout(() => { const ta = textareaRef.current; if (ta) { ta.focus(); ta.setSelectionRange(ta.value.length, ta.value.length) } }, 50)
  }

  // Check if save should be enabled
  const canSave = () => {
    if (saving) return false
    if (mode === 'new') {
      const lines = newNoteText.split('\n')
      const content = lines.slice(1).join('\n').trim()
      return content !== '' && asInserted
    }
    if (mode === 'edit') {
      return !!editingNoteId && editText.trim() !== ''
    }
    return false
  }


  // Render note with styled header and AS
  // [Bugfix 2026-04-28]: NO reordenar lineas "AS, ..." dentro del body. Antes el render
  // separaba todas las lineas AS y las pegaba al final, destruyendo el orden cronologico
  // cuando el agente pegaba historial con AS intercalados. El body se renderiza tal cual
  // se guardo. La asLine derivada de accion_siguiente solo se agrega si NO esta ya en el body.
  const renderStyledNote = (note) => {
    // Notas de Sistema (reagendamiento masivo): el body ya es UNA línea autocontenida
    // "d/m - Agente HH:MM AS, dd/mm" que incluye el nombre del agente que gatilló la acción.
    // Renderizamos solo el body (sin anteponer el header auto "dd/mm - Sistema HH:MM") para
    // no duplicar fecha/hora ni mostrar "Sistema" en vez del agente. Queda en una sola línea.
    if (note.autor === 'Sistema') {
      return <span>{(note.nota || '').trim()}</span>
    }
    const d = new Date(note.creada_en)
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const hh = String(d.getHours()).padStart(2, '0')
    const min = String(d.getMinutes()).padStart(2, '0')
    const header = dd + '/' + mm + ' - ' + (note.autor || 'Agente') + ' ' + hh + ':' + min
    const body = note.nota || ''
    let asLine = null
    if (note.accion_siguiente) {
      asLine = 'AS, ' + note.accion_siguiente
      if (note.accion_siguiente_fecha) {
        const fd = note.accion_siguiente_fecha.split('T')[0]
        const [y, m, da] = fd.split('-')
        asLine += ' ' + da + '/' + m
      }
    }
    // Evitar duplicar la asLine si el body ya la contiene literalmente como linea propia
    const bodyAlreadyHasAsLine = asLine && body.split('\n').some(l => l.trim() === asLine.trim())
    const hasBody = body.trim() !== ''

    return (
      <>
        <span>{header}</span>
        {hasBody && <>{'\n'}<span>{body}</span></>}
        {asLine && !bodyAlreadyHasAsLine && (
          <span>{'\n'}<span>{asLine}</span></span>
        )}
      </>
    )
  }

  return (
    <div className="border-t-2 border-gray-300 dark:border-surface-dark-border flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 bg-yellow-200 dark:bg-yellow-900/40 border-b border-yellow-300 dark:border-yellow-800 flex items-center justify-between">
        <span className="text-lg font-semibold text-gray-900 dark:text-gray-200 flex items-center gap-2">
          <span className="text-lg">📝</span>
          NOTAS <span className="text-base px-2.5 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">{notes.length}</span>
        </span>
        <div className="flex items-center gap-1">
          {mode === 'idle' ? (
            <>
              <button
                onClick={handleNewNote}
                className="px-4 py-1.5 text-sm font-semibold bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                title="Iniciar nueva nota"
              >
                + Nueva
              </button>
              {notes.length > 0 && (
                <button
                  onClick={handleEditMode}
                  className="px-4 py-1.5 text-sm font-semibold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                >
                  Editar
                </button>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancel}
                className="px-4 py-1.5 text-sm font-semibold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                Cancelar
              </button>
              {mode === 'edit' && editingNoteId && (
                <button
                  onClick={handleSave}
                  disabled={!canSave()}
                  className={`px-4 py-1.5 text-sm font-semibold text-white rounded transition disabled:opacity-40 disabled:cursor-not-allowed ${slowLevel === 2 ? 'bg-orange-600 hover:bg-orange-700' : slowLevel === 1 ? 'bg-amber-500 hover:bg-amber-600' : 'bg-green-500 hover:bg-green-600'}`}
                >
                  {saving ? (slowLevel === 2 ? 'A\u00fan guardando...' : slowLevel === 1 ? 'Conexi\u00f3n lenta...' : 'Guardando...') : 'Guardar'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sabana */}
      <div
        ref={sabanaRef} className="overflow-y-auto bg-white dark:bg-surface-dark min-h-[380px] max-h-[420px]"
        style={{ maxHeight: '420px' }}
      >
        <div className="sticky top-0 z-10 bg-yellow-50 dark:bg-yellow-900/80 border-b border-yellow-200 dark:border-yellow-800">
          {productInterest ? (
            <div className="px-3 py-1.5 text-lg text-gray-800 dark:text-gray-100 font-semibold">
              {productInterest}
            </div>
          ) : initialMessage ? (
            <div className="px-3 py-1.5 text-base text-gray-700 dark:text-gray-200 whitespace-pre-wrap italic">
              {initialMessage}
            </div>
          ) : null}
        </div>

        {productInterest && initialMessage && (
          <div className="px-3 py-1.5 text-base text-gray-700 dark:text-gray-200 whitespace-pre-wrap italic">
            {initialMessage}
          </div>
        )}

        {sortedNotes.map((note, idx) => (
          <div
            key={note.id || idx}
            onClick={() => handleNoteClick(note)}
            className={`px-3 py-3 text-base leading-relaxed border-b-4 border-blue-300 dark:border-blue-600/70 last:border-b-0 ${note.autor === 'Sistema' ? 'bg-pink-50 dark:bg-pink-900/20 ' : ''}${
              mode === 'edit' && editingNoteId !== note.id
                ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800/50'
                : ''
            } ${editingNoteId === note.id ? 'bg-blue-50 dark:bg-blue-900/50 py-2' : ''}`}
          >
            {editingNoteId === note.id ? (
              <textarea
                ref={textareaRef}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="w-full bg-white dark:bg-slate-800 border-2 border-blue-300 dark:border-blue-600 rounded p-2 text-base font-mono resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
                rows={5}
                autoFocus
              />
            ) : (
              <pre className="font-sans whitespace-pre-wrap m-0">{renderStyledNote(note)}</pre>
            )}
          </div>
        ))}

        {/* Textarea with quick-phrase sidebar */}
        <div className="px-3 py-2 border-t border-dashed border-gray-300 dark:border-gray-600">
          <div className="flex gap-1.5">
            <textarea
              ref={textareaRef}
              value={mode === 'new' ? newNoteText : ''}
              onChange={(e) => mode === 'new' && setNewNoteText(e.target.value)}
              readOnly={mode !== 'new'}
              onClick={mode === 'idle' ? handleNewNote : undefined}
              className={`flex-1 border-2 rounded p-2 text-base font-mono resize-none focus:outline-none ${
                mode === 'new'
                  ? 'bg-white dark:bg-slate-800 border-blue-300 dark:border-blue-600 focus:ring-1 focus:ring-blue-400'
                  : mode === 'idle'
                    ? 'bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-gray-700 text-gray-400 cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/10'
                    : 'bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-gray-700 text-gray-400 cursor-not-allowed'
              }`}
              rows={mode === 'new' ? 10 : 3}
              placeholder={mode === 'new' ? 'Escribe tu nota...' : 'Presiona "+ Nueva" o haz click aquí para escribir...'}
            />
            {(mode === 'new' || (mode === 'edit' && editingNoteId)) && (
              <div className="flex flex-col gap-1 pt-0.5">
                {/* === SALIENTES (verde): lo que el agente hace hacia el cliente === */}
                {/* Le llame - phone outgoing (green) */}
                <button
                  type="button"
                  onClick={() => insertQuickPhrase('le_llame', 'LE LLAM\u00C9')}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center border transition-all ${
                    pendingQuickPhrase.includes('le_llame')
                      ? 'bg-green-200 dark:bg-green-800/60 border-green-400 dark:border-green-500 ring-2 ring-green-400 dark:ring-green-500 shadow-md'
                      : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/40 hover:shadow-sm'
                  }`}
                  title={"Le llam\u00E9"}
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5">
                    <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.57.57a1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.57 3.57a1 1 0 01-.25 1.02l-2.2 2.2z" fill="#16A34A"/>
                    <path d="M18 4l-7 7M18 4v6M18 4h-6" stroke="#16A34A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </button>
                {/* Le escribi - WhatsApp outgoing (green) */}
                <button
                  type="button"
                  onClick={() => insertQuickPhrase('le_escribi', 'LE ESCRIB\u00CD')}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center border transition-all ${
                    pendingQuickPhrase.includes('le_escribi')
                      ? 'bg-green-200 dark:bg-green-800/60 border-green-400 dark:border-green-500 ring-2 ring-green-400 dark:ring-green-500 shadow-md'
                      : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/40 hover:shadow-sm'
                  }`}
                  title={"Le escrib\u00ED"}
                >
                  <svg viewBox="0 0 28 28" className="w-5 h-5">
                    <path d="M14 2.5C7.649 2.5 2.5 7.649 2.5 14c0 2.02.527 3.92 1.447 5.574L2.5 25.5l5.926-1.447A11.45 11.45 0 0014 25.5c6.351 0 11.5-5.149 11.5-11.5S20.351 2.5 14 2.5z" stroke="#16A34A" strokeWidth="1.6" fill="none"/>
                    <path d="M19.077 16.382c-.33-.165-1.954-.964-2.256-1.074-.303-.11-.523-.165-.744.165-.22.33-.853 1.074-1.045 1.294-.192.22-.385.248-.715.083-.33-.165-1.394-.514-2.656-1.638-.982-.875-1.644-1.956-1.837-2.286-.192-.33-.02-.509.145-.673.148-.148.33-.385.496-.578.165-.193.22-.33.33-.551.11-.22.055-.413-.028-.578-.083-.165-.744-1.791-1.019-2.453-.268-.643-.541-.556-.744-.566-.192-.01-.413-.012-.633-.012-.22 0-.578.083-.881.413-.303.33-1.155 1.129-1.155 2.754 0 1.624 1.183 3.193 1.348 3.413.165.22 2.33 3.556 5.643 4.986.788.34 1.403.543 1.882.695.791.252 1.511.217 2.08.131.634-.094 1.954-.799 2.23-1.569.275-.771.275-1.432.192-1.569-.083-.138-.303-.22-.633-.385z" fill="#16A34A"/>
                    <path d="M22 4l-8 8M22 4v7M22 4h-7" stroke="#16A34A" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </button>
                {/* Envie cotizacion - document outgoing (green) */}
                <button
                  type="button"
                  onClick={() => insertQuickPhrase('envie_cotizacion', 'ENVI\u00C9 COTIZACI\u00D3N')}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center border transition-all ${
                    pendingQuickPhrase.includes('envie_cotizacion')
                      ? 'bg-green-200 dark:bg-green-800/60 border-green-400 dark:border-green-500 ring-2 ring-green-400 dark:ring-green-500 shadow-md'
                      : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/40 hover:shadow-sm'
                  }`}
                  title={"Envi\u00E9 cotizaci\u00F3n"}
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5">
                    <rect x="2" y="3" width="12" height="16" rx="1.5" stroke="#16A34A" strokeWidth="1.5" fill="none"/>
                    <path d="M5 8h6M5 11h6M5 14h3" stroke="#16A34A" strokeWidth="1.3" strokeLinecap="round"/>
                    <path d="M20 4l-7 7M20 4v6M20 4h-6" stroke="#16A34A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </button>
                {/* Separador: salientes (arriba) / entrantes (abajo) */}
                <div className="h-px bg-gray-300 dark:bg-gray-600 mx-1 my-1" />
                {/* === ENTRANTES (\u00E1mbar): lo que el cliente hace hacia el agente === */}
                {/* Me llamo - phone incoming (amber) */}
                <button
                  type="button"
                  onClick={() => insertQuickPhrase('me_llamo', 'ME LLAM\u00D3')}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center border transition-all ${
                    pendingQuickPhrase.includes('me_llamo')
                      ? 'bg-amber-200 dark:bg-amber-800/60 border-amber-400 dark:border-amber-500 ring-2 ring-amber-400 dark:ring-amber-500 shadow-md'
                      : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40 hover:shadow-sm'
                  }`}
                  title={"Me llam\u00F3"}
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5">
                    <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.57.57a1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.57 3.57a1 1 0 01-.25 1.02l-2.2 2.2z" fill="#D97706"/>
                    <path d="M13 9l7-7M13 9V3M13 9h6" stroke="#D97706" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </button>
                {/* Me escribio - WhatsApp incoming (amber) */}
                <button
                  type="button"
                  onClick={() => insertQuickPhrase('me_escribio', 'ME ESCRIBI\u00D3')}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center border transition-all ${
                    pendingQuickPhrase.includes('me_escribio')
                      ? 'bg-amber-200 dark:bg-amber-800/60 border-amber-400 dark:border-amber-500 ring-2 ring-amber-400 dark:ring-amber-500 shadow-md'
                      : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40 hover:shadow-sm'
                  }`}
                  title={"Me escribi\u00F3"}
                >
                  <svg viewBox="0 0 28 28" className="w-5 h-5">
                    <path d="M14 2.5C7.649 2.5 2.5 7.649 2.5 14c0 2.02.527 3.92 1.447 5.574L2.5 25.5l5.926-1.447A11.45 11.45 0 0014 25.5c6.351 0 11.5-5.149 11.5-11.5S20.351 2.5 14 2.5z" stroke="#D97706" strokeWidth="1.6" fill="none"/>
                    <path d="M19.077 16.382c-.33-.165-1.954-.964-2.256-1.074-.303-.11-.523-.165-.744.165-.22.33-.853 1.074-1.045 1.294-.192.22-.385.248-.715.083-.33-.165-1.394-.514-2.656-1.638-.982-.875-1.644-1.956-1.837-2.286-.192-.33-.02-.509.145-.673.148-.148.33-.385.496-.578.165-.193.22-.33.33-.551.11-.22.055-.413-.028-.578-.083-.165-.744-1.791-1.019-2.453-.268-.643-.541-.556-.744-.566-.192-.01-.413-.012-.633-.012-.22 0-.578.083-.881.413-.303.33-1.155 1.129-1.155 2.754 0 1.624 1.183 3.193 1.348 3.413.165.22 2.33 3.556 5.643 4.986.788.34 1.403.543 1.882.695.791.252 1.511.217 2.08.131.634-.094 1.954-.799 2.23-1.569.275-.771.275-1.432.192-1.569-.083-.138-.303-.22-.633-.385z" fill="#D97706"/>
                    <path d="M17 9l8-8M17 9V2M17 9h7" stroke="#D97706" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </button>
                {/* Pidio cotizacion - document incoming (amber) */}
                <button
                  type="button"
                  onClick={() => insertQuickPhrase('pidio_cotizacion', 'PIDI\u00D3 COTIZACI\u00D3N')}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center border transition-all ${
                    pendingQuickPhrase.includes('pidio_cotizacion')
                      ? 'bg-amber-200 dark:bg-amber-800/60 border-amber-400 dark:border-amber-500 ring-2 ring-amber-400 dark:ring-amber-500 shadow-md'
                      : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40 hover:shadow-sm'
                  }`}
                  title={"Pidi\u00F3 cotizaci\u00F3n"}
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5">
                    <rect x="2" y="3" width="12" height="16" rx="1.5" stroke="#D97706" strokeWidth="1.5" fill="none"/>
                    <path d="M5 8h6M5 11h6M5 14h3" stroke="#D97706" strokeWidth="1.3" strokeLinecap="round"/>
                    <path d="M15 9l7-7M15 9V3M15 9h6" stroke="#D97706" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>

        {notes.length === 0 && !initialMessage && mode === 'idle' && (
          <div
            onClick={handleNewNote}
            className="py-6 text-center text-gray-400 dark:text-gray-500 text-xs cursor-pointer hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50/30 dark:hover:bg-blue-900/10 transition"
          >
            Sin notas. Presiona "+ Nueva" o haz click aquí para agregar la primera nota.
          </div>
        )}
      </div>

      {/* Bottom toolbar - Si Contesta / No Contesta / Si WSP / Agendar / Hoy / Guardar */}
      {mode !== 'idle' && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-t border-gray-200 dark:border-gray-700">
          <button onClick={handleSiContestaClick}
            className="px-3 py-1.5 text-sm font-medium rounded-lg border transition bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-600 hover:bg-green-200">
            Si Contesta
          </button>
          <button onClick={handleNoContestaClick}
            className="px-3 py-1.5 text-sm font-medium rounded-lg border transition bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-600 hover:bg-red-200">
            No Contesta
          </button>
          <button onClick={handleSiWspClick}
            className="px-3 py-1.5 text-sm font-medium rounded-lg border transition bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-400 dark:border-emerald-600 hover:bg-emerald-200">
            💬 Si WSP
          </button>
          <button onClick={handleAgendarClick}
            className="px-3 py-1.5 text-sm font-medium rounded-lg border transition bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-600 hover:bg-amber-200">
            Agendar
          </button>
          <button onClick={handleHoyClick} disabled={!canHoy()}
            className="px-3 py-1.5 text-sm font-medium rounded-lg border transition bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-600 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
            title={!nextFollowupDate ? 'Sin fecha de vencimiento — asigna una primero' : 'Guardar nota manteniendo la fecha de vencimiento actual'}>
            Hoy
          </button>
          <div className="flex-1" />
          <button onClick={handleSave} disabled={!canSave()}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg text-white disabled:opacity-50 ${slowLevel === 2 ? 'bg-orange-600 hover:bg-orange-700' : slowLevel === 1 ? 'bg-amber-500 hover:bg-amber-600' : 'bg-primary-600 hover:bg-primary-700'}`}>
            {saving ? (slowLevel === 2 ? 'A\u00fan guardando...' : slowLevel === 1 ? 'Conexi\u00f3n lenta...' : 'Guardando...') : 'Guardar'}
          </button>
        </div>
      )}

      {/* Edit hint */}
      {mode === 'edit' && !editingNoteId && (
        <div className="px-3 py-1 text-center text-[10px] text-blue-500 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/40">
          Haz click en una nota para editarla
        </div>
      )}

      {/* AS Date Picker Modal \u2014 componente compartido */}
      <DayPickerModal
        open={showAsDatePicker}
        onClose={() => { setShowAsDatePicker(false); setAsModalSource(null) }}
        source={asModalSource}
        currentDay={noContestaDay}
        initialDay={(asModalSource === 'si_contesta' || asModalSource === 'si_whatsapp') && Number(noContestaDay) > 0 ? 0 : null}
        asDate={asDate}
        setAsDate={setAsDate}
        asTime={asTime}
        setAsTime={setAsTime}
        lockDate={false}
        onConfirm={(day, date, time) => handleASConfirmCore(day, date, time)}
      />
    </div>
  )
}
